import io from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { SESSION_EXPIRED_MESSAGE } from '@nxsys/constants/session';
import { TOKEN } from '@nxsys/store/constants/keys';
import { resolveAuthTarget } from '@nxsys/utils/authTarget';

/**
 * The new-notification socket of the system at `host`, not connected yet:
 * connectNotificationSocket connects it once a session exists.
 *
 * socket.io-client shares one connection (a Manager) between the sockets of a
 * host, and that Manager takes its options from the first socket created on
 * it, so an `autoConnect: false` there would keep every later socket `io()`
 * makes for the host from ever connecting. This one gets a connection of its
 * own.
 */
export const createNotificationSocket = (host: string): Socket =>
  io(`${host}/new-notification`, {
    transports: ['websocket', 'polling'],
    autoConnect: false,
    forceNew: true,
  });

type Listener = (...args: any[]) => void;

/** Puts listeners on, and takes them off, every one of `sockets`. */
export const everySocket = (sockets: Socket[]) => ({
  on(event: string, listener: Listener) {
    sockets.forEach((s) => s.on(event, listener));
  },
  // socket.io counts the arguments: `off(event)` removes every listener of the
  // event, while `off(event, undefined)` removes only the first one.
  off(event: string, listener?: Listener) {
    sockets.forEach((s) => (listener ? s.off(event, listener) : s.off(event)));
  },
});

/**
 * NX-5877: connects the notification socket of the session's system
 * (`isBureau`) with the session's credentials and returns its teardown.
 *
 * The credentials are what the session's HTTP requests carry (ApiConfig): the
 * hand-off auth_code when there is one, the stored token otherwise. Every
 * attempt — the first one and each reconnect socket.io makes by itself after a
 * dropped connection — reads them afresh, so once a token has been renewed
 * (NX-5238) the renewed one is what the server sees. Storage is shared by every
 * tab, though, and a sign-in to the other system in another tab replaces what
 * is stored with that system's session. Its credentials are never presented
 * here: ids are minted per database, so they name someone else on this
 * system's server.
 *
 * socket.io neither retries a handshake the server refused nor reconnects a
 * socket the server disconnected, which it does once the session behind it is
 * no longer live. Both are retried here only when newer credentials have been
 * stored since that attempt — a refusal only when it names an expired session,
 * with the message HTTP uses for it. An expired token cannot be renewed
 * (`/auth/refresh` refuses it too), so with nothing newer the session is over
 * and is left to end the way it does on HTTP. Any other refusal — no token, a
 * forged or revoked one, a blocked user — is final.
 */
export function connectNotificationSocket(
  socket: Socket,
  isBureau: boolean,
): () => void {
  const stored = () => {
    const target = resolveAuthTarget();
    if (target.isBureau !== isBureau) return { authCode: null, token: null };
    return { authCode: target.authCode, token: localStorage.getItem(TOKEN) };
  };

  let presented: string | null = null;
  socket.auth = (cb) => {
    const { authCode, token } = stored();
    presented = authCode || token;
    cb(authCode ? { authCode } : { token });
  };

  const retryIfRenewed = () => {
    const { authCode, token } = stored();
    const renewed = authCode || token;
    if (renewed && renewed !== presented) socket.connect();
  };

  const onConnectError = (error: Error) => {
    // Still active: a network failure, which socket.io retries by itself.
    if (socket.active || error.message !== SESSION_EXPIRED_MESSAGE) return;
    retryIfRenewed();
  };

  const onDisconnect = (reason: Socket.DisconnectReason) => {
    if (reason === 'io server disconnect') retryIfRenewed();
  };

  socket.on('connect_error', onConnectError);
  socket.on('disconnect', onDisconnect);
  socket.connect();
  return () => {
    socket.off('connect_error', onConnectError);
    socket.off('disconnect', onDisconnect);
    socket.disconnect();
  };
}
